import { useEventUserRole } from '@shared/components/AuthProvider';
import { useUnlockDialogContext } from '@apps/guest/packages/layout-engine/components/UnlockDialog/UnlockDialog.context';
import { useCtaButtons } from '@apps/guest/packages/layout-engine/common/utils/useCtaButtons';
import { useAuthUpgradeRefetch } from '@shared/hooks/useAuthUpgradeQuery';
import { useEffectiveGqlCacheData } from '@shared/hooks/useEffectiveGqlCacheData';
import { useIsGuestSiteSsrEnabled } from '@apps/guest/shared/hooks/useIsGuestSiteSsrEnabled';
import queryString from '@shared/core/queryString';
import { useFetchScheduleListData } from './useFetchScheduleListData';

// Use the explicit ?preview=true query param rather than isInIframe() — iframes can appear
// in other legitimate contexts (e.g. masked/custom domains) that are not preview sessions.
const isPreviewing = queryString.getValueString('preview') === 'true';

export const useScheduleListController = (eventHandle: string) => {
  const { hasIdentifiedUserOnce } = useEventUserRole();
  const { handleUnlockDialogOpen } = useUnlockDialogContext();
  const { getUnlockButtonText } = useCtaButtons();
  const isGuestSiteSsrEnabled = useIsGuestSiteSsrEnabled();

  // In preview mode (isPreviewing = true), SSR is disabled and GetGuestSiteSchedule fires
  // immediately with isAdmin: false before GetMe resolves. Skip until auth is known to
  // prevent refetchQueries from landing with the wrong isAdmin variable.
  const { data, previousData, loading, refetch } = useFetchScheduleListData(eventHandle, isPreviewing && !hasIdentifiedUserOnce);

  // SSR public-first: When SSR renders /schedule, the query fires with anonymous context.
  // activeSession fields (eventCTA.scheduleHasInviteOnlyItems, person, hasUnlocked) will
  // reflect anonymous defaults. After GetMe resolves, refetch to get auth-aware data
  // (invite-only filtering, guest identity).
  useAuthUpgradeRefetch(isGuestSiteSsrEnabled ? refetch : undefined);

  // When isAdmin variable changes (false → true after GetMe), Apollo sees a new cache key
  // and data becomes undefined until the network response arrives. useEffectiveGqlCacheData
  // falls back to previousData during this transition and suppresses loading when we have
  // usable data — preventing UI flash and loading oscillation from cache-and-network.
  const { effectiveData, loading: effectiveLoading } = useEffectiveGqlCacheData({ data, previousData, loading });

  const { activeSession, formattedSchedules, toBeAnnouncedItems } = effectiveData ?? {};
  const { eventCTA, hasUnlocked, person } = activeSession || {};

  const tbaSchedule = toBeAnnouncedItems ?? [];
  const formattedSchedule = formattedSchedules ?? [];
  const shouldDisplayUnlockButton = !isPreviewing && eventCTA?.scheduleHasInviteOnlyItems && !person?.fullName?.length;
  const unlockButtonText = getUnlockButtonText(hasUnlocked);

  return {
    formattedSchedule,
    shouldDisplayUnlockButton,
    loading: effectiveLoading,
    tbaSchedule,
    handleUnlockDialogClick: () => handleUnlockDialogOpen(true),
    unlockButtonText,
  };
};
